import React, { useState, useEffect } from 'react';
import { X, Settings } from 'lucide-react';

const CookieBanner = () => {
    // Initialisiere showBanner basierend auf localStorage (falls verfügbar)
    const [showBanner, setShowBanner] = useState(() => {
        if (typeof window !== 'undefined') {
            const consent = localStorage.getItem('cookie_consent');
            return !consent; // Zeige Banner wenn KEIN consent gespeichert ist
        }
        return true; // Default: zeige Banner
    });
    const [showSettings, setShowSettings] = useState(false);
    useEffect(() => {
        // Zeige Banner nur, wenn noch keine Entscheidung getroffen wurde
        const consent = localStorage.getItem('cookie_consent');
        if (!consent) {
            setShowBanner(true);
        } else {
            setShowBanner(false);
            if (consent === 'accepted') loadInsights();
        }
    }, []);

    // Eigene Besucherstatistik (liegt auf dem Bürokult-Server, keine Drittanbieter).
    // Wird nur nach Zustimmung geladen; das Skript prüft die Zustimmung selbst noch einmal.
    const loadInsights = () => {
        if (window.bkInsights) {
            if (!window.bkInsights.active) window.bkInsights.start();
            return;
        }
        if (document.getElementById('bk-insights')) return;
        const script = document.createElement('script');
        script.id = 'bk-insights';
        script.src = '/api/insights/t.js';
        script.async = true;
        script.dataset.site = 'teamwerk';
        document.head.appendChild(script);
    };

    const stopInsights = () => {
        if (window.bkInsights) window.bkInsights.stop();
    };

    const handleAccept = () => {
        localStorage.setItem('cookie_consent', 'accepted');
        localStorage.setItem('cookie_consent_date', new Date().toISOString());
        // Banner ausblenden nach Akzeptieren
        setShowBanner(false);
        setShowSettings(false);
        loadInsights();
    };

    const handleDecline = () => {
        localStorage.setItem('cookie_consent', 'declined');
        localStorage.setItem('cookie_consent_date', new Date().toISOString());
        // Banner ausblenden nach Ablehnen
        setShowBanner(false);
        setShowSettings(false);
        stopInsights();
    };

    const handleOpenSettings = () => {
        setShowSettings(true);
        // Banner bleibt sichtbar, damit Benutzer bei jedem Besuch die Möglichkeit hat
    };

    const handleCloseSettings = () => {
        setShowSettings(false);
        // Banner bleibt immer sichtbar
        setShowBanner(true);
    };

    const handleRevokeConsent = () => {
        localStorage.removeItem('cookie_consent');
        localStorage.removeItem('cookie_consent_date');
        setShowSettings(false);
        // Banner bleibt immer sichtbar
        setShowBanner(true);
        stopInsights();
    };

    // Cookie-Einstellungen Panel
    if (showSettings) {
        const consent = localStorage.getItem('cookie_consent');
        return (
            <div className="fixed bottom-0 left-0 right-0 z-[9999] p-6 bg-black/98 backdrop-blur-sm shadow-2xl border-t border-white/10">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-white text-lg font-bold flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Cookie-Einstellungen
                        </h3>
                        <button
                            onClick={handleCloseSettings}
                            className="text-white/70 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-4 mb-4">
                        <p className="text-white text-sm mb-4">
                            Mit Ihrer Zustimmung nutzen wir eine eigene Besucherstatistik. Sie läuft auf unserem eigenen Server, es werden keine Daten an Dritte übermittelt und keine IP-Adressen gespeichert.
                        </p>
                        
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-white font-medium text-sm">Besucherstatistik</p>
                                    <p className="text-white/60 text-xs">Eigene Statistik ohne Drittanbieter: besuchte Seiten, Verweildauer, Herkunft. Zufällige Kennung im Browser, keine IP-Speicherung.</p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${consent === 'accepted' ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'}`}>
                                    {consent === 'accepted' ? 'Aktiviert' : 'Deaktiviert'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {consent === 'accepted' ? (
                            <>
                                <button
                                    onClick={handleRevokeConsent}
                                    className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/30 rounded-lg hover:border-white/50 transition-colors"
                                >
                                    Zustimmung widerrufen
                                </button>
                                <button
                                    onClick={handleCloseSettings}
                                    className="px-6 py-2 text-sm bg-white text-black font-medium rounded-lg hover:bg-white/90 transition-colors"
                                >
                                    Schließen
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleDecline}
                                    className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/30 rounded-lg hover:border-white/50 transition-colors"
                                >
                                    Alle ablehnen
                                </button>
                                <button
                                    onClick={handleAccept}
                                    className="px-6 py-2 text-sm bg-white text-black font-medium rounded-lg hover:bg-white/90 transition-colors"
                                >
                                    Alle akzeptieren
                                </button>
                            </>
                        )}
                    </div>
                    
                    <p className="text-white/50 text-xs mt-4">
                        <a href="/datenschutz" className="underline hover:text-white/70">Datenschutzerklärung</a>
                    </p>
                </div>
            </div>
        );
    }

    // Banner wird immer angezeigt, daher wird dieser Block nie erreicht
    // Aber für den Fall der Fälle behalten wir den Settings-Button
    if (!showBanner && !showSettings) {
        // Zeige Cookie-Einstellungen Button als Fallback
        const consent = localStorage.getItem('cookie_consent');
        if (consent) {
            return (
                <button
                    onClick={handleOpenSettings}
                    className="fixed bottom-4 right-4 z-[9998] p-3 bg-black/80 hover:bg-black text-white rounded-full shadow-lg transition-all hover:scale-110"
                    title="Cookie-Einstellungen"
                    aria-label="Cookie-Einstellungen öffnen"
                >
                    <Settings className="w-5 h-5" />
                </button>
            );
        }
    }

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 bg-black/95 backdrop-blur-sm shadow-2xl border-t border-white/10 animate-slide-up">
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex-1 text-white text-sm">
                    <p className="font-medium mb-1">🍪 Cookie-Einstellungen</p>
                    <p className="text-white/70 text-xs sm:text-sm">
                        Mit Ihrer Zustimmung nutzen wir eine eigene Besucherstatistik ohne Drittanbieter, um unsere Website zu verbessern. 
                        <a href="/datenschutz" className="underline hover:text-white ml-1">Mehr erfahren</a>
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleOpenSettings}
                        className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/30 rounded-lg hover:border-white/50 transition-colors"
                    >
                        Einstellungen
                    </button>
                    <button
                        onClick={handleDecline}
                        className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/30 rounded-lg hover:border-white/50 transition-colors"
                    >
                        Ablehnen
                    </button>
                    <button
                        onClick={handleAccept}
                        className="px-6 py-2 text-sm bg-white text-black font-medium rounded-lg hover:bg-white/90 transition-colors"
                    >
                        Akzeptieren
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieBanner;
